import type { UserLike } from '@/Ship/Helpers/fieldViews'

/**
 * Права картки клієнта — `_permissions` у відповіді з `?permissions=true`
 * (`SPEC-009` §9.2). Фронт показує дії лише за ними, ролей сам не питає
 * (`SPEC-003` §1.3): `ClientRule` знає і правило відповідального (КЛ-13), і
 * звужений перегляд (КЛ-12), а ролі з входу — ні.
 */
export interface ClientPermissions {
    read?: boolean
    update?: boolean
    delete?: boolean
    comment?: boolean
    send_to_verification?: boolean
    verify?: boolean
    reject_verification?: boolean
    blacklist?: boolean
    unblacklist?: boolean
}

/** Статус клієнта — вкладений рядок довідника (`status_list:short:read`). */
export interface ClientStatusRef {
    '@id'?: string
    uuid?: string
    code?: string
    title?: string
    color?: string
}

/** Значення картки так, як їх тримає форма (лише те, що читає фронт). */
export interface ClientCardValues {
    uuid?: string
    '@id'?: string
    title?: string
    status?: ClientStatusRef | null
    /** Верифікований — обчислюване зі статусу (`SPEC-009` §5.3.4); від нього — «Друковані форми» (КЛ-26) */
    isVerified?: boolean
    phones?: string[] | null
    emails?: string[] | null
    responsibles?: UserLike[] | null
    _permissions?: ClientPermissions
    [key: string]: unknown
}

/** Те, що шаблон картки бере з ядрового `formContext`. */
export interface ClientFormContextLike {
    isEditable?: boolean
    values?: ClientCardValues
    getError?: (path: string) => unknown
    setInitialValue?: (path: string, value: unknown) => void
}

/** Те, що шаблон картки бере з ядрового `crudContext`. */
export interface ClientCrudContextLike {
    resource?: { key?: string; restUrl?: string; fields?: Record<string, unknown> }
}

/** Колекція клієнтів — від неї й адреса історії (`/services/clients/{uuid}/history`). */
export const CLIENT_REST_URL = '/services/clients'

/** Контакти клієнта (`SPEC-009` §6.3): префільтр `client.uuid`, ресурс `Crm.ClientContact`. */
export const CLIENT_CONTACT_REST_URL = '/services/client_contacts'

/** Коментарі клієнта (`SPEC-009` §6.3, ADR 027): поле власника — `client`. */
export const CLIENT_COMMENT_REST_URL = '/services/client_comments'

/** Коментарі контакту клієнта: поле власника — `contact`. */
export const CLIENT_CONTACT_COMMENT_REST_URL = '/services/client_contact_comments'

/** Ключ ресурсу контакту клієнта — під ним його відкриває вкладка «Контакти». */
export const CLIENT_CONTACT_RESOURCE = 'Crm.ClientContact'

/** Договори клієнта (`SPEC-009` §6.4): префільтр `client.uuid`, ресурс `Crm.ClientContract`. */
export const CLIENT_CONTRACT_REST_URL = '/services/client_contracts'

/** Крок 1 двокрокового завантаження файлу договору (`SPEC-008` §5.7.1). */
export const CLIENT_CONTRACT_FILE_UPLOAD_URL = '/services/client_contract_files'

/** Ключ ресурсу договору клієнта — під ним його відкриває вкладка «Договори». */
export const CLIENT_CONTRACT_RESOURCE = 'Crm.ClientContract'

/** Документи клієнта (`SPEC-009` §6.5): префільтр `client.uuid`, ресурс `Crm.ClientDocument`. */
export const CLIENT_DOCUMENT_REST_URL = '/services/client_documents'

/** Ключ ресурсу документа клієнта — під ним його відкриває вкладка «Друковані форми». */
export const CLIENT_DOCUMENT_RESOURCE = 'Crm.ClientDocument'
